import type { Messages } from "../../../core/api";
import {
  replacePlaceholders,
  type AnswerObject,
  type ContentItem,
  type PlaceholderValues,
} from "../layout/documentLayout";

// Pure answer formatting for the review-document preview. It mirrors how the server PDF adapter
// (`mfDocumentAdapter.ts`) turns each stored answer into printed text, so views stay thin.
// Only `import type` from core/api so the module loads in node tests without the app runtime.

export type PatientDocumentAnswer = Messages["PatientDocumentAnswer"];
export type WeightAnswer = Messages["WeightAnswer"];
type ItemType = ContentItem["itemType"];

export interface ChoiceOption {
  checked: boolean;
  label: string;
}

export const OTHER_LABEL = "Other";
export const DIGITAL_SIGNATURE_CONSENT_MARKER = "__DIGITAL_SIGNATURE_CONSENT__";
const YES_NO_OPTIONS = ["Yes", "No"];

export function firstAnswer(answers: AnswerObject[]): PatientDocumentAnswer | undefined {
  return answers[0]?.answer;
}

/** Like the server: placeholders are replaced in string answers (and, here, string-array items). */
export function replaceInAnswer(
  answer: PatientDocumentAnswer | undefined,
  placeholders: PlaceholderValues
): PatientDocumentAnswer | undefined {
  if (typeof answer === "string") {
    return replacePlaceholders(answer, placeholders);
  }
  if (Array.isArray(answer) && answer.every((item) => typeof item === "string")) {
    return (answer as string[]).map((item) => replacePlaceholders(item, placeholders));
  }
  return answer;
}

export function answerAsString(answer: unknown): string | null {
  if (typeof answer === "string") {
    return answer;
  }
  if (typeof answer === "number") {
    return String(answer);
  }
  return null;
}

export function stringItems(answer: unknown): string[] {
  if (Array.isArray(answer)) {
    return (answer as unknown[]).filter((item): item is string => typeof item === "string");
  }
  return typeof answer === "string" && answer !== "" ? [answer] : [];
}

/** Option labels of a choice question; yesNo questions fall back to Yes / No like mobile. */
export function choiceOptions(question: ContentItem): string[] {
  const options = question.possibleAnswers ?? [];
  if (question.itemType === "yesNo" && options.length === 0) {
    return YES_NO_OPTIONS;
  }
  return options;
}

/** "yyyy-MM-dd..." (string or js-joda LocalDate/Instant) -> "MM/dd/yyyy"; anything else as-is. */
export function formatAmericanDate(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  const text = String(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  return match === null ? text : `${match[2]}/${match[3]}/${match[1]}`;
}

export function isWeightAnswer(value: unknown): value is WeightAnswer {
  return (
    typeof value === "object" &&
    value !== null &&
    "amount" in value &&
    "unit" in value &&
    typeof (value as WeightAnswer).amount === "number"
  );
}

export function formatWeight(answer: unknown): string {
  return isWeightAnswer(answer) ? `${answer.amount} ${answer.unit}` : "";
}

/** Stored as `[systolic, diastolic]`; the PDF prints "N/A" when there is nothing to show. */
export function formatBloodPressure(answer: unknown): string {
  const parts = stringItems(answer);
  if (!Array.isArray(answer) || parts.every((part) => part === "")) {
    return "N/A";
  }
  return parts.join(" / ");
}

export type TextItemType = Extract<
  ItemType,
  "textShort" | "textLong" | "number" | "date" | "time" | "bloodPressure" | "weight"
>;

const TEXT_ITEM_TYPES = new Set<ItemType>([
  "textShort",
  "textLong",
  "number",
  "date",
  "time",
  "bloodPressure",
  "weight",
]);

export function isTextItemType(itemType: ItemType): itemType is TextItemType {
  return TEXT_ITEM_TYPES.has(itemType);
}

export function formatTextAnswer(itemType: TextItemType, answer: unknown): string {
  switch (itemType) {
    case "textShort":
    case "textLong":
    case "number":
    case "time":
      return answerAsString(answer) ?? "";
    case "date":
      return formatAmericanDate(answerAsString(answer));
    case "bloodPressure":
      return formatBloodPressure(answer);
    case "weight":
      return formatWeight(answer);
    default: {
      const unhandled: never = itemType;
      return unhandled;
    }
  }
}

/**
 * Radio / yesNo / dropDown, exactly as the PDF prints them (mfDocumentAdapter convertRadioToMfBlock):
 * an option is checked only when it is the stored answer itself. With `showOtherOption`, a stored
 * string that is not an option is the "Other" text: "Other: <text>", or a checked plain "Other"
 * for an empty string.
 */
export function resolveRadioOptions(params: {
  answer: unknown;
  options: string[];
  showOtherOption: boolean;
}): ChoiceOption[] {
  const { answer, options, showOtherOption } = params;
  const result = options.map((label) => ({ label, checked: label === answer }));
  if (showOtherOption) {
    const isOther = typeof answer === "string" && !options.includes(answer);
    result.push(
      isOther
        ? { label: answer === "" ? OTHER_LABEL : `${OTHER_LABEL}: ${answer}`, checked: true }
        : { label: OTHER_LABEL, checked: false }
    );
  }
  return result;
}

/**
 * Check, exactly as the PDF prints it (mfDocumentAdapter convertCheckToMfBlock): only a stored
 * array checks options (a plain string checks nothing). With `showOtherOption`, the first value
 * that is not an option is the "Other" text ("Other" when it is empty).
 */
export function resolveCheckOptions(params: {
  answer: unknown;
  options: string[];
  showOtherOption: boolean;
}): ChoiceOption[] {
  const { answer, options, showOtherOption } = params;
  const selected: unknown[] = Array.isArray(answer) ? (answer as unknown[]) : [];
  const result = options.map((label) => ({ label, checked: selected.includes(label) }));
  if (showOtherOption) {
    const otherIndex = selected.findIndex(
      (value) => typeof value !== "string" || !options.includes(value)
    );
    if (otherIndex === -1) {
      result.push({ label: OTHER_LABEL, checked: false });
    } else {
      const other = selected[otherIndex];
      result.push({
        label: other === "" ? OTHER_LABEL : `${OTHER_LABEL}: ${String(other)}`,
        checked: true,
      });
    }
  }
  return result;
}

/** Grid columns of the printed options (renderDocument.ts data-item-count rules). */
export function optionColumns(params: {
  count: number;
  inDifferentLines: boolean;
  width: number | null;
}): number {
  const count = Math.min(params.count, 4);
  if (params.inDifferentLines) {
    return 1;
  }
  if (params.width === 1 && count === 4) {
    return 3;
  }
  return Math.max(count, 1);
}

// ---- Editor round-trip helpers ----

export const OTHER_VALUE = "__document_review_other__";

export interface RadioEditorState {
  otherText: string;
  /** an option, OTHER_VALUE, or null when nothing is selected */
  selected: string | null;
}

export function splitRadioAnswer(params: {
  answer: unknown;
  options: string[];
  showOtherOption: boolean;
}): RadioEditorState {
  const { answer, options, showOtherOption } = params;
  const value = Array.isArray(answer) ? (answer as unknown[])[0] : answer;
  if (typeof value !== "string" || value === "") {
    return { selected: null, otherText: "" };
  }
  if (options.includes(value)) {
    return { selected: value, otherText: "" };
  }
  return showOtherOption
    ? { selected: OTHER_VALUE, otherText: value }
    : { selected: null, otherText: "" };
}

/** The stored radio answer is the option itself, or the "Other" free text itself. */
export function buildRadioAnswer(state: RadioEditorState): string | null {
  return state.selected === OTHER_VALUE ? state.otherText : state.selected;
}

export interface CheckEditorState {
  /** values not in the options that are not the "Other" text; kept so a save never drops data */
  extras: string[];
  otherChecked: boolean;
  otherText: string;
  selected: string[];
}

export function splitCheckAnswer(params: {
  answer: unknown;
  options: string[];
  showOtherOption: boolean;
}): CheckEditorState {
  const { answer, options, showOtherOption } = params;
  const values = stringItems(answer);
  const selected = values.filter((value) => options.includes(value));
  const unknown = values.filter((value) => !options.includes(value));
  if (showOtherOption && unknown.length > 0) {
    return { selected, otherChecked: true, otherText: unknown[0], extras: unknown.slice(1) };
  }
  return { selected, otherChecked: false, otherText: "", extras: unknown };
}

/**
 * Options in question order, then the "Other" free text, then preserved extras. The Other text
 * must come before the extras: the view and the PDF treat the first non-option value as "Other".
 */
export function buildCheckAnswer(options: string[], state: CheckEditorState): string[] {
  return [
    ...options.filter((option) => state.selected.includes(option)),
    ...(state.otherChecked ? [state.otherText] : []),
    ...state.extras,
  ];
}

/** Reads a property of an untyped (JSON) answer object. */
export function readField(value: unknown, key: string): unknown {
  if (typeof value !== "object" || value === null) {
    return undefined;
  }
  return (value as Record<string, unknown>)[key];
}
